/**
 * ---------------------------------------------------------------------------
 * PERSONNALISATION PRODUIT
 * ---------------------------------------------------------------------------
 * Élément personnalisé <personnalisation-produit> rendu à l'intérieur du vrai
 * formulaire d'ajout au panier (snippets/buy-buttons.liquid).
 *
 * Rôle :
 *  - nettoyer les saisies (espaces superflus en début / fin et doublons internes) ;
 *  - bloquer l'ajout au panier tant qu'un champ obligatoire est vide, avec un
 *    message d'erreur clair en français ;
 *  - désactiver les champs facultatifs restés vides juste avant l'envoi, afin
 *    qu'ils n'apparaissent pas dans le panier, le checkout ni la commande.
 *
 * Les accents, apostrophes et tirets sont acceptés : aucune restriction de
 * caractères n'est appliquée, seule la longueur maximale est limitée.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('personnalisation-produit')) return;

  /** Supprime les espaces de début / fin et réduit les espaces multiples. */
  function nettoyer(valeur) {
    return String(valeur == null ? '' : valeur)
      .replace(/[\t\r\n]+/g, ' ')
      .replace(/ {2,}/g, ' ')
      .trim();
  }

  class PersonnalisationProduit extends HTMLElement {
    connectedCallback() {
      if (this._initialise) return;
      this._initialise = true;

      this.formulaire = this.closest('form');
      if (!this.formulaire) return;

      this.zoneErreur = this.querySelector('[data-perso-error]');
      this.champs = Array.prototype.slice.call(this.querySelectorAll('[data-perso-field]'));
      if (this.champs.length === 0) return;

      this._surEnvoi = this._surEnvoi.bind(this);
      this._surInvalide = this._surInvalide.bind(this);
      this._surClicPaiement = this._surClicPaiement.bind(this);

      // Phase de capture sur `document` : notre contrôle passe systématiquement
      // avant le gestionnaire d'ajout au panier du thème, quel que soit l'ordre
      // d'enregistrement des écouteurs.
      document.addEventListener('submit', this._surEnvoi, true);

      // Neutralise l'infobulle native du navigateur au profit de notre message.
      this.formulaire.addEventListener('invalid', this._surInvalide, true);

      // Les boutons de paiement accéléré (Shop Pay, PayPal…) court-circuitent
      // l'envoi du formulaire : on les intercepte au clic.
      this.formulaire.addEventListener('click', this._surClicPaiement, true);

      this.champs.forEach((champ) => {
        champ.addEventListener('blur', () => {
          champ.value = nettoyer(champ.value);
        });
        champ.addEventListener('input', () => {
          this._effacerErreurChamp(champ);
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this._surEnvoi, true);
      if (this.formulaire) {
        this.formulaire.removeEventListener('invalid', this._surInvalide, true);
        this.formulaire.removeEventListener('click', this._surClicPaiement, true);
      }
    }

    /**
     * Nettoie et contrôle tous les champs.
     * @returns {boolean} true si le formulaire peut être envoyé.
     */
    valider() {
      let premierInvalide = null;

      this.champs.forEach((champ) => {
        champ.value = nettoyer(champ.value);

        const obligatoire = champ.dataset.persoRequired === 'true';
        const vide = champ.value === '';

        if (obligatoire && vide) {
          champ.setAttribute('aria-invalid', 'true');
          champ.classList.add('perso__field-input--invalid');
          if (!premierInvalide) premierInvalide = champ;
        } else {
          this._effacerErreurChamp(champ);
        }
      });

      if (premierInvalide) {
        const libelle = premierInvalide.dataset.persoLabel || 'ce champ';
        this._afficherErreur(
          'Merci d’indiquer « ' + libelle + ' » avant d’ajouter le produit au panier.'
        );

        premierInvalide.focus({ preventScroll: true });
        premierInvalide.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      this._masquerErreur();
      return true;
    }

    _surEnvoi(event) {
      if (event.target !== this.formulaire) return;

      if (!this.valider()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      // Les champs facultatifs vides ne doivent pas être envoyés : un champ
      // désactivé est ignoré par le navigateur comme par `new FormData(form)`.
      const neutralises = this.champs.filter((champ) => champ.value === '' && !champ.disabled);
      neutralises.forEach((champ) => {
        champ.disabled = true;
      });

      // Réactivation une fois l'envoi (classique ou AJAX) sérialisé.
      setTimeout(() => {
        neutralises.forEach((champ) => {
          champ.disabled = false;
        });
      }, 0);
    }

    _surInvalide(event) {
      if (!this.champs.includes(event.target)) return;

      // Empêche l'infobulle native (dont la langue dépend du navigateur).
      event.preventDefault();
      this.valider();
    }

    _surClicPaiement(event) {
      const cible = event.target;
      if (!cible || typeof cible.closest !== 'function') return;

      const bouton = cible.closest('.shopify-payment-button, .shopify-payment-button__button');
      if (!bouton) return;

      if (!this.valider()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    _afficherErreur(message) {
      if (!this.zoneErreur) return;
      this.zoneErreur.textContent = message;
      this.zoneErreur.hidden = false;
    }

    _masquerErreur() {
      if (!this.zoneErreur) return;
      this.zoneErreur.textContent = '';
      this.zoneErreur.hidden = true;
    }

    _effacerErreurChamp(champ) {
      champ.removeAttribute('aria-invalid');
      champ.classList.remove('perso__field-input--invalid');

      const resteInvalide = this.champs.some(
        (autre) => autre.getAttribute('aria-invalid') === 'true'
      );
      if (!resteInvalide) this._masquerErreur();
    }
  }

  customElements.define('personnalisation-produit', PersonnalisationProduit);
})();
